import type {} from '../type/global.d.ts';
import type {} from '../type/coreViteEnv.d.ts';

// 번역 라이브러리(Tolgee) 어댑터입니다. 번역 라이브러리를 직접 import 하는 유일한 파일이며
// (.oxlintrc.json 의 no-restricted-imports 가 강제), 앱 코드는 여기서 내보내는 API 만 씁니다.
// 라이브러리를 바꿀 때는 이 파일의 공개 API 시그니처만 유지하면 됩니다.
import { FormatIcu } from '@tolgee/format-icu';
import {
  DevTools,
  GlobalContextPlugin,
  T,
  Tolgee,
  type TolgeeStaticData,
  useTolgee,
  useTranslate,
} from '@tolgee/react';
import {
  type ReactElement,
  type ReactNode,
  createElement,
  useCallback,
} from 'react';

import { APP_CONST } from '../constant';
import type en from '../locales/en.json';
import { storage } from '@vueron/kiska/util';

export type TranslationKey = keyof typeof en;

export type TranslationParamValue = Nullish<
  bigint | boolean | Date | number | string
>;

export type TranslationParams = Record<string, TranslationParamValue>;

// 서버 값 등 동적 키도 받도록 string 을 허용하고, 리터럴은 TranslationKey 로 자동완성됩니다.
export type TranslateFn = (
  key: TranslationKey | (string & {}),
  params?: TranslationParams,
) => string;

export type TransParamValue =
  | ReactElement
  | TranslationParamValue
  | ((chunk: ReactNode) => ReactElement | null);

type TransProps = {
  i18nKey: TranslationKey;
  params?: Record<string, TransParamValue>;
};

const DEFAULT_LANGUAGE = APP_CONST.LANG.EN;

const LANGUAGE_LIST = Object.values(APP_CONST.LANG);

const toSupportedLanguage = (language: Nullish<string>): APP_CONST.LANG =>
  LANGUAGE_LIST.find(item => item === language) ?? DEFAULT_LANGUAGE;

// 번역 JSON 은 같은 오리진의 언어별 청크로 지연 로드합니다. 외부 요청이 없어 내부망(오프라인)에서도 동작하며,
// glob 이라 번역 파일이 없는 worktree/테스트에서도 import 오류가 나지 않습니다.
const LOCALE_STATIC_DATA: TolgeeStaticData = Object.fromEntries(
  Object.entries(
    import.meta.glob<Record<string, string>>('../locales/*.json', {
      import: 'default',
    }),
  ).map(([path, load]) => [path.replace(/^.*\/(\w+)\.json$/, '$1'), load]),
);

// in-context 편집(Alt + 클릭) 자격 증명은 dev 서버 전용입니다. DEV 가드로 production 번들에서 제거됩니다.
const inContextCredential =
  import.meta.env.DEV &&
  import.meta.env.MODE !== 'test' &&
  import.meta.env.VITE_TOLGEE_API_KEY &&
  import.meta.env.VITE_TOLGEE_API_URL
    ? {
        apiKey: import.meta.env.VITE_TOLGEE_API_KEY,
        apiUrl: import.meta.env.VITE_TOLGEE_API_URL,
      }
    : null;

const tolgeeChain = Tolgee()
  .use(GlobalContextPlugin({ useSuspense: false }))
  .use(FormatIcu());

// DevTools 는 개발 빌드에서 자격 증명이 없어도 문자열에 보이지 않는 표시 문자를 붙이므로
// in-context 편집을 쓸 때만 붙입니다.
const tolgee = (
  inContextCredential ? tolgeeChain.use(DevTools()) : tolgeeChain
).init({
  ...inContextCredential,
  availableLanguages: LANGUAGE_LIST,
  // 저장된 언어(STORAGE_LANG)로 시작해 langStore 가 로드되기 전에 렌더되는 초기 UI 도 같은 언어로 보이게 합니다.
  defaultLanguage: toSupportedLanguage(
    storage.local.get<string>(APP_CONST.KEYS.STORAGE_LANG),
  ),
  fallbackLanguage: DEFAULT_LANGUAGE,
  // 파라미터가 빠지는 등 포맷에 실패하면 Tolgee 기본값('invalid') 대신 번역 원문을 보여 줍니다.
  // i18next 시절처럼 placeholder 가 그대로 보이는 쪽이 화면에서 원인을 찾기 쉽습니다.
  onFormatError: (_error, { key, translation }) => translation ?? key,
});

const pluralRulesMap = new Map<string, Intl.PluralRules>();

const getPluralRules = (language: string) => {
  const cached = pluralRulesMap.get(language);

  if (cached) return cached;

  const pluralRules = new Intl.PluralRules(language);
  pluralRulesMap.set(language, pluralRules);

  return pluralRules;
};

export const getLanguage = () => toSupportedLanguage(tolgee.getLanguage());

// i18next 시절 복수형 키(<key>_one, <key>_other)를 이어 씁니다. count 가 숫자이고 현재 언어에
// 접미사 키가 있을 때만 그 키로 번역합니다. 대체 언어(en)의 접미사 키는 쓰지 않아 ko 화면에 영어가 섞이지 않습니다.
const toPluralKey = (key: string, params?: TranslationParams) => {
  const count = params?.count;

  if (typeof count !== 'number') return key;

  const language = getLanguage();
  const translationMap = tolgee.getRecord({ language, namespace: '' })?.data;
  const pluralKey = `${key}_${getPluralRules(language).select(count)}`;

  return translationMap?.[pluralKey] == null ? key : pluralKey;
};

let runPromise: Nullish<Promise<void>>;

// 여러 엔트리(main, demo)에서 불러도 번역은 한 번만 로드합니다.
export const initI18n = (staticData: TolgeeStaticData = LOCALE_STATIC_DATA) => {
  if (!runPromise) {
    tolgee.addStaticData(staticData);
    runPromise = tolgee.run();
  }

  return runPromise;
};

export const changeLanguage = async (language: string) => {
  await tolgee.changeLanguage(toSupportedLanguage(language));
};

// React 밖(스토어, 유틸)에서 쓰는 번역 함수입니다. 언어가 바뀌어도 다시 렌더링되지 않으므로
// 컴포넌트에서는 useTranslation 을 씁니다.
export const translate: TranslateFn = (key, params) =>
  tolgee.t(toPluralKey(key, params), params);

export const useTranslation = () => {
  const { t } = useTranslate();
  const language = toSupportedLanguage(useTolgee(['language']).getLanguage());

  // useTranslate 의 t 는 언어가 바뀌어도 참조가 같습니다. language 를 deps 에 둬서
  // t 를 deps 로 쓰는 메모 값이 언어 변경 시 다시 계산되게 합니다.
  const translateInHook = useCallback<TranslateFn>(
    (key, params) => t(toPluralKey(key, params), params),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [t, language],
  );

  return {
    language,
    t: translateInHook,
  };
};

export const Trans = ({ i18nKey, params }: TransProps) =>
  createElement(T, { keyName: i18nKey, params });

Trans.displayName = 'Trans';
